import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Clock, Trash2, Video, Loader2, Share, FileDown, Search, X, Film, Globe, History as HistoryIcon, Play, DollarSign, RotateCcw, ArchiveRestore, Eye, ShoppingCart, Star, MessageSquare, Flag, Megaphone, Coins, Heart, Home, BarChart3, TrendingUp, Mic, GraduationCap, Download, Smartphone, MonitorPlay, Copy, MessageCircle, Languages, Images } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { shareManual } from "@/lib/share";
import { VideoUploadModal } from "@/components/VideoUploadModal";
import StudioDashboard from "@/components/StudioDashboard";
import RenderTracker from "@/components/RenderTracker";
import BrandName from "@/components/BrandName";
import MobileCreateButton from "@/components/MobileCreateButton";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LANG_LABELS = { en: "English", es: "Spanish", fr: "French", de: "German", it: "Italian", zh: "Chinese", ja: "Japanese", pt: "Portuguese" };

export default function MyVideos() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = !!user?.unlimited;
  const [videos, setVideos] = useState(null);
  const [videoBusy, setVideoBusy] = useState(null);
  const [imagesBusy, setImagesBusy] = useState(null);
  const [amazonBusy, setAmazonBusy] = useState(null);
  const [dupBusy, setDupBusy] = useState(null);
  const [query, setQuery] = useState("");
  const [trendsOpen, setTrendsOpen] = useState(false);
  const [orientFilter, setOrientFilter] = useState("all"); // all | portrait | landscape
  const [sortBy, setSortBy] = useState("newest");          // newest | oldest
  const [catFilter, setCatFilter] = useState("all");
  const [langFilter, setLangFilter] = useState("all");
  const [langCopyFor, setLangCopyFor] = useState(null);    // manual pending a language-copy
  const [langCopyBusy, setLangCopyBusy] = useState(false);
  const [editManual, setEditManual] = useState(null);
  const [postBusy, setPostBusy] = useState(null);

  const [preview, setPreview] = useState(null); // { title, url }
  const [previewLoading, setPreviewLoading] = useState(null);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  // Tapping "My Videos" in the header while already on this page must return to the Archive
  // (not stay stuck on the Deleted panel). navigate() to the same route changes location.key.
  useEffect(() => { setShowTrash(false); }, [location.key]);
  const [trashBusy, setTrashBusy] = useState(null);
  const [stats, setStats] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);  // video pending delete-confirmation
  const [confirmExpert, setConfirmExpert] = useState(null); // video pending AI Expert purchase/top-up
  const [expertBusy, setExpertBusy] = useState(false);

  const buyExpert = async (m) => {
    if (expertBusy) return;
    setExpertBusy(true);
    try {
      const { data } = await api.post(`/manuals/${m.id}/expert/enable`);
      setVideos((vs) => (vs || []).map((v) => (v.id === m.id ? { ...v, expert: data.expert } : v)));
      setEditManual((em) => (em && em.id === m.id ? { ...em, expert: data.expert } : em));
      toast.success(m.expert?.enabled ? "Added 500 more questions!" : "AI Expert is live on this video! 500 questions ready.");
      setConfirmExpert(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add the Expert — check your balance.");
    } finally {
      setExpertBusy(false);
    }
  };

  const loadStats = () =>
    api.get("/me/studio/analytics").then(({ data }) => setStats(data)).catch(() => setStats(null));
  const statById = useMemo(
    () => Object.fromEntries((stats?.videos || []).map((v) => [v.id, v])),
    [stats]
  );

  const PRICE_OPTS = [0.99, 1.99, 2.99, 3.99, 4.99, 5.99, 6.99, 7.99];
  const savePrice = async (id, val) => {
    const price = parseFloat(val) || 0;
    setStats((s) => (s ? { ...s, videos: (s.videos || []).map((v) => (v.id === id ? { ...v, bundle_price: price } : v)) } : s));
    try {
      await api.post(`/manuals/${id}/edit`, { bundle_price: price });
      toast.success(price > 0 ? `Kit price set to $${price.toFixed(2)}` : "Kit set to Free");
    } catch {
      toast.error("Couldn't update the price");
    }
  };

  const loadTrash = () =>
    api.get("/manuals/trash").then(({ data }) => setTrash(data || [])).catch(() => setTrash([]));

  const openPreview = async (e, m) => {
    e.stopPropagation();
    if (previewLoading) return;
    setPreviewLoading(m.id);
    try {
      const res = await api.get(`/manuals/${m.id}/video/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreview({ title: m.title || "Your video", url });
    } catch {
      toast.error("Couldn't load the video preview");
    } finally {
      setPreviewLoading(null);
    }
  };

  const closePreview = () => {
    setPreview((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });
  };

  // "My Videos" = the user's Vittyyo videos (anything with a rendered video). Editable hub.
  const load = () =>
    api.get("/manuals").then(({ data }) => setVideos((data || []).filter((m) => m.has_video || m.assets_credited || m.lang_copy_of))).catch(() => setVideos([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { loadTrash(); }, []);
  useEffect(() => { loadStats(); }, []);

  const availableCats = useMemo(() => Array.from(new Set((videos || []).map((m) => m.category).filter(Boolean))).sort(), [videos]);
  const availableLangs = useMemo(() => Array.from(new Set((videos || []).map((m) => (m.language || "en")))).sort(), [videos]);

  // Tally of how many videos fall under each orientation — shown on the filter toggles.
  const orientCounts = useMemo(() => {
    const list = videos || [];
    const isPortrait = (m) => m.mode === "minute" || m.orientation === "portrait";
    const portrait = list.filter(isPortrait).length;
    return { portrait, landscape: list.length - portrait };
  }, [videos]);

  const filtered = useMemo(() => {
    if (!videos) return videos;
    let list = videos;
    const isPortrait = (m) => m.mode === "minute" || m.orientation === "portrait";
    if (orientFilter === "portrait") list = list.filter(isPortrait);
    else if (orientFilter === "landscape") list = list.filter((m) => !isPortrait(m));
    if (catFilter !== "all") list = list.filter((m) => (m.category || "") === catFilter);
    if (langFilter !== "all") list = list.filter((m) => (m.language || "en") === langFilter);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((m) => (m.title || "").toLowerCase().includes(q) || (m.channel || "").toLowerCase().includes(q) || (m.category || "").toLowerCase().includes(q));
    const sorted = [...list].sort((a, b) => {
      const da = a.created_at || "", db = b.created_at || "";
      return sortBy === "oldest" ? (da < db ? -1 : da > db ? 1 : 0) : (da > db ? -1 : da < db ? 1 : 0);
    });
    return sorted;
  }, [videos, query, orientFilter, catFilter, langFilter, sortBy]);

  // Archive pagination — 60 videos per page (page 2 = 61–120, page 3 = 121–180, …)
  const PAGE_SIZE = 60;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [query]);
  useEffect(() => { setPage(1); }, [orientFilter]);
  useEffect(() => { setPage(1); }, [catFilter, langFilter, sortBy]);
  const totalPages = filtered ? Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)) : 1;
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paged = filtered ? filtered.slice(pageStart, pageStart + PAGE_SIZE) : filtered;

  const togglePost = async (e, m) => {
    e.stopPropagation();
    setPostBusy(m.id);
    try {
      await api.post(`/manuals/${m.id}/share`, {
        shared: !m.shared,
        category: m.share_category || m.category || "Lifestyle",
        source_url: m.source_url || "",
        channel: m.channel || "",
      });
      toast.success(!m.shared ? "Posted to Community — it's live! 🎉" : "Unpublished from Community");
      setVideos((list) => list.map((x) => (x.id === m.id ? { ...x, shared: !m.shared } : x)));
      setEditManual((em) => (em && em.id === m.id ? { ...em, shared: !m.shared } : em));
    } catch {
      toast.error("Couldn't update — try again");
    } finally {
      setPostBusy(null);
    }
  };

  const [demoBusy, setDemoBusy] = useState(null);
  const makeLanguageCopy = async (m, lang) => {
    if (langCopyBusy) return;
    setLangCopyBusy(true);
    try {
      const { data } = await api.post(`/manuals/${m.id}/language-copy`, { language: lang });
      toast.success(`Created a ${LANG_LABELS[lang] || lang.toUpperCase()} copy — render it when you're ready 🎬`);
      setLangCopyFor(null);
      setEditManual(null);
      navigate(data.mode === "minute" ? `/minute/${data.id}` : `/manual/${data.id}/complete`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the copy — try again");
    } finally {
      setLangCopyBusy(false);
    }
  };
  const duplicateVideo = async (e, m) => {
    e.stopPropagation();
    setDupBusy(m.id);
    try {
      const { data } = await api.post(`/manuals/${m.id}/duplicate`);
      toast.success("Duplicated — edit your fresh copy ✂️");
      navigate(data.mode === "minute" ? `/minute/${data.id}` : `/manual/${data.id}/complete`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't duplicate — try again");
    } finally {
      setDupBusy(null);
    }
  };
  const setAsDemo = async (e, m) => {
    e.stopPropagation();
    setDemoBusy(m.id);
    try {
      await api.post("/admin/set-demo", { manual_id: m.id });
      toast.success(`"${m.title}" is now the "See how it works" demo 🎬`);
      setVideos((list) => list.map((x) => ({ ...x, is_demo: x.id === m.id })));
      setEditManual((em) => (em && em.id === m.id ? { ...em, is_demo: true } : em));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set the demo — try again");
    } finally {
      setDemoBusy(null);
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/manuals/${id}/restore`);
      load();
      loadTrash();
      toast.success("Video restored");
    } catch {
      toast.error("Couldn't restore — it may have expired");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/manuals/${id}`);
      setVideos((m) => m.filter((x) => x.id !== id));
      loadTrash();
      toast.success("Video removed", {
        description: "You can recover it for the next 14 days.",
        action: { label: "Undo", onClick: () => restore(id) },
        duration: 8000,
      });
    } catch {
      toast.error("Could not remove");
    }
  };

  const restoreFromTrash = async (id) => {
    setTrashBusy(id);
    try {
      await api.post(`/manuals/${id}/restore`);
      setTrash((t) => t.filter((x) => x.id !== id));
      load();
      toast.success("Video restored to My Videos");
    } catch {
      toast.error("Couldn't restore — try again");
    } finally {
      setTrashBusy(null);
    }
  };

  const purgeForever = async (id) => {
    if (!window.confirm("Delete this video forever? This can't be undone.")) return;
    setTrashBusy(id);
    try {
      await api.delete(`/manuals/trash/${id}`);
      setTrash((t) => t.filter((x) => x.id !== id));
      toast.success("Deleted forever");
    } catch {
      toast.error("Couldn't delete — try again");
    } finally {
      setTrashBusy(null);
    }
  };

  const share = (e, m) => {
    e.stopPropagation();
    const shareUrl = m.shared ? `${window.location.origin}/community/${m.id}` : window.location.origin;
    shareManual({ title: m.title, steps: new Array(m.num_steps || 0), shareUrl });
  };

  const doVideoDownload = async (e, m) => {
    e.stopPropagation();
    setVideoBusy(m.id);
    try {
      const res = await api.get(`/manuals/${m.id}/video/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(m.title || "video").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50) || "video"}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No video file to download");
    } finally {
      setVideoBusy(null);
    }
  };

  const doThumbnailDownload = (e, m) => {
    e.stopPropagation();
    const tid = m.custom_cover_id || m.hero_image_id;
    if (!tid) { toast.error("No thumbnail to download yet"); return; }
    try {
      const fname = `stepmystep-thumbnail-${(m.title || "video").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "video"}.jpg`;
      const a = document.createElement("a");
      a.href = `${imageUrl(tid)}?dl=1&name=${encodeURIComponent(fname)}`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Couldn't download that thumbnail");
    }
  };

  const doImagesDownload = async (e, m) => {
    e.stopPropagation();
    setImagesBusy(m.id);
    try {
      const res = await api.get(`/manuals/${m.id}/images/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(m.title || "video").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50) || "video"}-images.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No images to download yet");
    } finally {
      setImagesBusy(null);
    }
  };

  const doAmazonDownload = async (e, m) => {
    e.stopPropagation();
    if (!m.has_amazon) { toast.error("No Amazon links on this video yet"); return; }
    setAmazonBusy(m.id);
    toast.loading("Building your Amazon link sheet…", { id: "amzn" });
    try {
      const res = await api.get(`/manuals/${m.id}/amazon-links.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(m.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50) || "manual"}-amazon-links.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Amazon link sheet saved", { id: "amzn" });
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "No Amazon links for this video yet." : "Couldn't build the Amazon link sheet", { id: "amzn" });
    } finally {
      setAmazonBusy(null);
    }
  };

  // Power toolbar shown at the top of the edit lightbox — all the old per-video icon controls, in one place.
  const renderEditControls = (m) => {
    const Btn = ({ testid, onClick, title, active, danger, yellow, disabled, icon, label }) => (
      <button
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); onClick(e); }}
        disabled={disabled}
        title={title}
        className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-xl transition-colors disabled:opacity-50 ${
          danger ? "text-[#f0736f] hover:bg-[#c14b4b]/15"
          : yellow ? "text-[#f5c542] bg-[#f5c542]/12 ring-1 ring-[#f5c542]/45 hover:bg-[#f5c542]/20"
          : active ? "text-[#6b8dff] hover:bg-[#2563eb]/20"
          : "text-[#8fbf82] hover:bg-[#5d8a52]/15"
        }`}
      >
        {icon}
        <span className="text-[10px] font-bold leading-tight text-center">{label}</span>
      </button>
    );
    return (
      <div className="grid grid-cols-5 gap-1 justify-items-center" data-testid={`edit-controls-${m.id}`}>
        {isAdmin && (
          <Btn
            testid={`post-community-${m.id}`}
            onClick={(e) => togglePost(e, m)}
            disabled={postBusy === m.id}
            active={m.shared}
            title={m.shared ? "Live in Community — tap to unpublish" : "Post to Community"}
            icon={postBusy === m.id ? <Loader2 size={19} className="spin" /> : <Globe size={19} />}
            label={m.shared ? "Live" : "Post"}
          />
        )}
        <Btn
          testid={`open-completion-${m.id}`}
          onClick={() => navigate(m.mode === "minute" ? `/minute/${m.id}` : `/manual/${m.id}/complete`)}
          yellow
          title={m.mode === "minute" ? "Edit this reel in the Minute Mode editor" : "Open the editor — thumbnail, audio, re-render"}
          icon={<Film size={19} />}
          label={m.mode === "minute" ? "Edit Reel" : "Editor"}
        />
        <Btn
          testid={`play-video-${m.id}`}
          onClick={(e) => openPreview(e, m)}
          disabled={previewLoading === m.id}
          title="Play a preview"
          icon={previewLoading === m.id ? <Loader2 size={19} className="spin" /> : <Play size={19} />}
          label="Preview"
        />
        <Btn
          testid={`share-manual-${m.id}`}
          onClick={(e) => share(e, m)}
          title="Share"
          icon={<Share size={19} />}
          label="Share"
        />
        <Btn
          testid={`amazon-download-${m.id}`}
          onClick={(e) => doAmazonDownload(e, m)}
          disabled={!m.has_amazon || amazonBusy === m.id}
          active={m.has_amazon}
          title={m.has_amazon ? "Download a PDF of this video's Amazon affiliate links" : "No Amazon links yet"}
          icon={amazonBusy === m.id ? <Loader2 size={19} className="spin" /> : <DollarSign size={19} className={m.has_amazon ? "text-[#4ade80]" : ""} />}
          label="Amazon"
        />
        <Btn
          testid={`video-download-${m.id}`}
          onClick={(e) => doVideoDownload(e, m)}
          disabled={videoBusy === m.id}
          title="Download the finished MP4 video"
          icon={videoBusy === m.id ? <Loader2 size={19} className="spin" /> : <FileDown size={19} />}
          label={<>Download<br />Video</>}
        />
        <Btn
          testid={`thumbnail-download-${m.id}`}
          onClick={(e) => doThumbnailDownload(e, m)}
          disabled={!(m.custom_cover_id || m.hero_image_id)}
          title="Download the thumbnail image"
          icon={<Download size={19} />}
          label={<>Download<br />Thumbnail</>}
        />
        <Btn
          testid={`images-download-${m.id}`}
          onClick={(e) => doImagesDownload(e, m)}
          disabled={imagesBusy === m.id}
          title="Download all the photos used in this video (cover, steps & finish) as a zip"
          icon={imagesBusy === m.id ? <Loader2 size={19} className="spin" /> : <Images size={19} />}
          label={<>Download<br />Images</>}
        />
        {isAdmin && (
          <Btn
            testid={`set-demo-${m.id}`}
            onClick={(e) => setAsDemo(e, m)}
            disabled={demoBusy === m.id}
            active={false}
            title={m.is_demo ? "This is the current 'See how it works' demo" : "Use as the 'See how it works' demo"}
            icon={demoBusy === m.id ? <Loader2 size={19} className="spin" /> : <Megaphone size={19} className={m.is_demo ? "text-[#c2410c]" : ""} />}
            label={m.is_demo ? "Demo ✓" : "Demo"}
          />
        )}
        <Btn
          testid={`duplicate-manual-${m.id}`}
          onClick={(e) => duplicateVideo(e, m)}
          disabled={dupBusy === m.id}
          title="Duplicate this video into a fresh, editable copy"
          icon={dupBusy === m.id ? <Loader2 size={19} className="spin" /> : <Copy size={19} />}
          label="Duplicate"
        />
        {m.mode !== "minute" && (
          <Btn
            testid={`language-copy-${m.id}`}
            onClick={() => setLangCopyFor(m)}
            title="Make a copy of this video in another language (keeps this one)"
            icon={<Languages size={19} />}
            label={<>Language<br />Copy</>}
          />
        )}
        <Btn
          testid={`expert-toggle-${m.id}`}
          onClick={() => { setEditManual(null); setConfirmExpert(m); }}
          active={m.expert?.enabled}
          title={m.expert?.enabled ? `AI Expert live — ${m.expert.used}/${m.expert.quota} questions used` : "Add an AI Expert chatbot to this video"}
          icon={<MessageCircle size={19} className={m.expert?.enabled ? "text-[#6b8dff]" : ""} />}
          label={m.expert?.enabled ? <>Expert<br />{m.expert.used} / {m.expert.quota}</> : <>Add AI<br />Expert</>}
        />
        <Btn
          testid={`delete-manual-${m.id}`}
          onClick={() => { setEditManual(null); setConfirmDel(m); }}
          danger
          title="Remove video"
          icon={<Trash2 size={19} />}
          label="Delete"
        />
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-3 pb-16">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2 min-w-0 text-white" data-testid="my-videos-title">
          <BrandName dark /> <span className="inline-flex items-center gap-2 whitespace-nowrap sms-outline-dark">DASHBOARD <Video size={22} className="hidden sm:inline text-white shrink-0" /></span>
        </h1>
        <div className="flex items-center gap-5 sm:gap-6">
          {(showTrash || !videos?.length) && <MobileCreateButton />}
          <RenderTracker onItemReady={load} />
          <button
            data-testid="go-home-btn"
            onClick={() => navigate("/")}
            title="Home"
            className="text-sm font-semibold text-white hover:text-[#d7e6cf] flex items-center gap-1.5 transition-colors"
          >
            <Home size={26} /> <span className="hidden sm:inline">Home</span>
          </button>
          <button
            data-testid="go-history-btn"
            onClick={() => navigate("/history")}
            title="View history"
            className="text-sm font-semibold text-white hover:text-[#d7e6cf] flex items-center gap-1.5 transition-colors"
          >
            <HistoryIcon size={26} /> <span className="hidden sm:inline">History</span>
          </button>
          {trash.length > 0 && (
            <button
              data-testid="toggle-trash-btn"
              onClick={() => setShowTrash((v) => !v)}
              title="Recently deleted"
              className={`text-sm font-semibold flex items-center gap-1.5 transition-colors ${showTrash ? "text-[#b91c1c]" : "text-[#dc2626] hover:text-[#b91c1c]"}`}
            >
              <Trash2 size={26} /> <span className="hidden sm:inline">Deleted</span>
              <span className="tabular-nums bg-[#fdeef0] text-[#c22f45] rounded-full px-1.5 py-0.5 text-[11px] font-bold">{trash.length}</span>
            </button>
          )}
        </div>
      </div>

      {!showTrash && videos?.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5 sm:gap-2" data-testid="myvideos-sort-pills">
          <select
            data-testid="sort-newest-pill"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs sm:text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-3 sm:px-4 py-2 outline-none cursor-pointer focus:border-[#1e3aaa] transition-colors"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
          <select
            data-testid="sort-category-pill"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            className="text-xs sm:text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-3 sm:px-4 py-2 outline-none cursor-pointer focus:border-[#1e3aaa] transition-colors"
          >
            <option value="all">All categories</option>
            {availableCats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <MobileCreateButton className="ml-auto" />
          <div className="w-full sm:w-auto flex items-center gap-1.5 sm:contents">
          <select
            data-testid="sort-language-pill"
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value)}
            className="shrink-0 text-xs sm:text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-2.5 sm:px-4 py-2 outline-none cursor-pointer focus:border-[#1e3aaa] transition-colors"
          >
            <option value="all">All languages</option>
            {availableLangs.map((l) => <option key={l} value={l}>{LANG_LABELS[l] || l.toUpperCase()}</option>)}
          </select>
            <button
              data-testid="toggle-portrait-btn"
              onClick={() => setOrientFilter((v) => (v === "portrait" ? "all" : "portrait"))}
              aria-pressed={orientFilter === "portrait"}
              title={orientFilter === "portrait" ? "Showing Minute Mode reels only — tap to show all" : "Show only Minute Mode (portrait) reels"}
              className="flex-1 sm:flex-none shrink-0 flex items-center justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-extrabold rounded-full px-2 sm:px-4 py-2 transition-transform hover:scale-[1.03] whitespace-nowrap"
              style={orientFilter === "portrait" ? { background: "#ffd21e", color: "#171426", border: "2px solid #171426" } : { background: "#14141b", color: "#fff", border: "2px solid #5d8a52" }}
            >
              <Smartphone size={15} className="hidden min-[400px]:inline sm:inline" /> Portrait
              <span
                data-testid="portrait-count"
                className="text-[10px] font-extrabold tabular-nums px-1.5 py-0.5 rounded-full leading-none"
                style={orientFilter === "portrait" ? { background: "rgba(23,20,38,.15)", color: "#171426" } : { background: "rgba(255,255,255,.12)", color: "#fff" }}
              >
                {orientCounts.portrait}
              </span>
            </button>
            <button
              data-testid="toggle-landscape-btn"
              onClick={() => setOrientFilter((v) => (v === "landscape" ? "all" : "landscape"))}
              aria-pressed={orientFilter === "landscape"}
              title={orientFilter === "landscape" ? "Showing Flagship & Creator videos only — tap to show all" : "Show only Flagship & Creator (landscape) videos"}
              className="flex-1 sm:flex-none shrink-0 flex items-center justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-extrabold rounded-full px-2 sm:px-4 py-2 transition-transform hover:scale-[1.03] whitespace-nowrap"
              style={orientFilter === "landscape" ? { background: "#ffd21e", color: "#171426", border: "2px solid #171426" } : { background: "#14141b", color: "#fff", border: "2px solid #5d8a52" }}
            >
              <MonitorPlay size={15} className="hidden min-[400px]:inline sm:inline" /> Landscape
              <span
                data-testid="landscape-count"
                className="text-[10px] font-extrabold tabular-nums px-1.5 py-0.5 rounded-full leading-none"
                style={orientFilter === "landscape" ? { background: "rgba(23,20,38,.15)", color: "#171426" } : { background: "rgba(255,255,255,.12)", color: "#fff" }}
              >
                {orientCounts.landscape}
              </span>
            </button>
          </div>
        </div>
      )}

      {!showTrash && videos !== null && videos.length > 0 && (
        <StudioDashboard stats={stats} onOpen={(id) => navigate(`/community/${id}`)} onChanged={loadStats} trendsOpen={trendsOpen} onCloseTrends={() => setTrendsOpen(false)} />
      )}

      {!showTrash && videos?.length > 0 && (
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center justify-center gap-2 sm:contents">
            {/* SHELVED — My Podcasts button in holding bin
            <button
              data-testid="go-my-podcasts-btn"
              onClick={() => navigate("/my-podcasts")}
              className="shrink-0 flex items-center justify-center gap-1.5 text-xs sm:text-sm font-extrabold text-white rounded-full px-5 py-2.5 transition-transform hover:scale-[1.03] whitespace-nowrap"
              style={{ background: "#1e3aaa", border: "2px solid #16308c" }}
            >
              <Mic size={15} /> <span>My Podcasts</span>
            </button>
            */}
            <button
              data-testid="toggle-trends-btn"
              onClick={() => setTrendsOpen((v) => !v)}
              className="shrink-0 flex items-center justify-center gap-1.5 text-xs sm:text-sm font-extrabold text-white rounded-full px-5 py-2.5 transition-transform hover:scale-[1.03] whitespace-nowrap"
              style={{ background: "#1e3aaa", border: "2px solid #16308c" }}
            >
              <TrendingUp size={15} /> Trends
            </button>
            <button
              data-testid="go-to-analytics-btn"
              onClick={() => navigate("/analytics")}
              className="shrink-0 flex items-center justify-center gap-1.5 text-xs sm:text-sm font-extrabold text-white rounded-full px-5 py-2.5 transition-transform hover:scale-[1.03] whitespace-nowrap"
              style={{ background: "#1e3aaa", border: "2px solid #16308c" }}
            >
              <BarChart3 size={15} /> <span className="sm:hidden">Analytics</span><span className="hidden sm:inline">Go To Analytics</span>
            </button>
          </div>
          <div className="relative w-full sm:flex-1 sm:min-w-0 order-last sm:order-none">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white" />
            <input
              data-testid="videos-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full text-sm bg-black text-white placeholder:text-white/50 border-2 border-[#5d8a52] rounded-full pl-9 pr-9 py-2.5 outline-none focus:border-[#1e3aaa] transition-colors"
            />
            {query && (
              <button data-testid="videos-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"><X size={15} /></button>
            )}
          </div>
        </div>
      )}

      {!showTrash && (videos === null ? (
        <div className="py-20 flex justify-center"><Loader2 size={28} className="spin text-[#5d8a52]" /></div>
      ) : videos.length === 0 ? (
        <div className="rounded-3xl p-10 text-center text-white/70" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }}>
          <p className="mb-4">No videos yet. Create your first one in the studio!</p>
          {isAdmin && (
            <button onClick={() => navigate("/studio")} className="sms-gradient-btn text-white font-bold px-6 py-3 rounded-2xl">
              Create a video
            </button>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm font-semibold text-white mb-3" data-testid="video-count-note">
            {query
              ? `${filtered.length} result${filtered.length === 1 ? "" : "s"} for "${query}"`
              : `${videos.length} uploaded video${videos.length === 1 ? "" : "s"}`}
            {totalPages > 1 && (
              <span className="text-[#9a96ac] font-medium"> · showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} (page {safePage} of {totalPages})</span>
            )}
          </p>
          {filtered.length === 0 ? (
            <div className="rounded-3xl p-8 text-center text-white/60" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="no-results">Nothing matches "{query}".</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5" data-testid="videos-grid">
              {paged.map((m) => (
                <div
                  key={m.id}
                  data-testid={`myvideo-item-${m.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (!m.has_video && m.lang_copy_of) { navigate(`/manual/${m.id}/complete`); } else { setEditManual(m); } }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!m.has_video && m.lang_copy_of) { navigate(`/manual/${m.id}/complete`); } else { setEditManual(m); } } }}
                  className="sms-tweed text-left cursor-pointer group rounded-2xl bg-[#0e0e13] border-2 border-[#5d8a52] p-3 shadow-md transition-shadow hover:shadow-xl"
                >
                  <div className="flex items-start gap-2 mb-1.5">
                    <div className="flex-1 font-bold text-[16px] leading-snug line-clamp-2 h-[2.9rem] text-white group-hover:text-[#e4f0e4] transition-colors" data-testid={`myvideo-title-${m.id}`} title={m.title}>
                      {m.title || "Untitled video"}
                    </div>
                    <button
                      type="button"
                      data-testid={`myvideo-trash-${m.id}`}
                      title="Delete from My Videos"
                      onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${m.title || "this video"}"? It moves to Recently Deleted (recoverable for 14 days).`)) remove(m.id); }}
                      className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[#e0455c] hover:text-white hover:bg-[#dc2626] transition-colors"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                  <div
                    className="relative w-full aspect-video rounded-xl bg-black overflow-hidden transition-shadow group-hover:shadow-lg"
                    style={{ border: "2px solid #5d8a52" }}
                  >
                    {(m.custom_cover_id || m.hero_image_id) && <img src={imageUrl(m.custom_cover_id || m.hero_image_id)} alt="" className={`w-full h-full ${m.mode === "minute" || m.orientation === "portrait" ? "object-contain" : "object-cover"}`} />}
                    <div className="absolute top-1.5 left-1.5 z-20 flex items-center gap-1">
                      <span className="text-[10px] font-extrabold text-white bg-black rounded-full px-2 py-0.5 shadow" data-testid={`myvideo-lang-${m.id}`} title={LANG_LABELS[m.language || "en"] || "English"}>
                        {(m.language || "en").toUpperCase()}
                      </span>
                      {(m.mode === "minute" || m.orientation === "portrait") && (
                        <span className="flex items-center gap-1 text-[10px] font-extrabold text-black bg-[#ffd21e] rounded-full px-2 py-0.5 shadow" data-testid={`myvideo-portrait-pill-${m.id}`}>
                          9:16 · 60s
                        </span>
                      )}
                    </div>
                    {m.format === "podcast" && (
                      <span className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 text-[10px] font-extrabold text-white bg-[#2563eb] rounded-full px-2 py-0.5 shadow" data-testid={`myvideo-podcast-pill-${m.id}`}>
                        <Mic size={11} /> Podcast
                      </span>
                    )}
                    {!m.has_video && m.assets_credited && (
                      <span className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 text-[10px] font-extrabold text-[#0f0f16] bg-[#8fbf82] rounded-full px-2 py-0.5 shadow" data-testid={`myvideo-assetonly-pill-${m.id}`}>
                        <GraduationCap size={11} /> Asset Only
                      </span>
                    )}
                    {!m.has_video && m.lang_copy_of && (
                      <span className="absolute bottom-1.5 left-1.5 z-20 flex items-center gap-1 text-[10px] font-extrabold text-white bg-[#dc2626] rounded-full px-2 py-0.5 shadow" data-testid={`myvideo-needsrender-pill-${m.id}`}>
                        <Video size={11} /> Tap to render
                      </span>
                    )}
                    {m.has_video && (m.shared ? (
                      <button
                        type="button"
                        onClick={(e) => togglePost(e, m)}
                        disabled={postBusy === m.id}
                        className="absolute bottom-1.5 right-1.5 z-20 flex items-center gap-1 text-[10px] font-extrabold text-white bg-[#2563eb] hover:bg-[#1d4ed8] rounded-full px-1.5 py-0.5 transition-colors disabled:opacity-60"
                        title="Live in Community — tap to unpublish"
                        data-testid={`myvideo-live-${m.id}`}
                      >
                        {postBusy === m.id ? <Loader2 size={11} className="spin" /> : <Globe size={11} />} LIVE
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => togglePost(e, m)}
                        disabled={postBusy === m.id}
                        className="absolute bottom-1.5 right-1.5 z-20 flex items-center gap-1 text-[10px] font-extrabold text-[#3a2c00] bg-[#eab308] hover:bg-[#ca8a04] rounded-full px-1.5 py-0.5 transition-colors disabled:opacity-60"
                        title="Not published — tap to go live in the Community"
                        data-testid={`myvideo-notlive-${m.id}`}
                      >
                        {postBusy === m.id ? <Loader2 size={11} className="spin" /> : <Globe size={11} />} NOT LIVE
                      </button>
                    ))}
                    {!m.has_video && m.assets_credited && (
                      <button
                        data-testid={`grid-openkit-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/manual/${m.id}/kit`); }}
                        title="Open your teaching kit"
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 h-10 rounded-full bg-[#4f7746] text-white text-xs font-extrabold shadow-lg ring-2 ring-white/70 opacity-95 hover:opacity-100 hover:scale-105 transition-transform z-10"
                      >
                        <Download size={15} /> Open Kit
                      </button>
                    )}
                    {m.has_video && (
                      <button
                        data-testid={`grid-play-${m.id}`}
                        onClick={(e) => openPreview(e, m)}
                        title="Play a preview"
                        aria-label="Play a preview"
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#4f7746] flex items-center justify-center shadow-lg ring-2 ring-white/70 opacity-90 hover:opacity-100 hover:scale-105 transition-transform z-10"
                      >
                        {previewLoading === m.id
                          ? <Loader2 size={18} className="spin text-white" />
                          : <Play size={18} className="text-white ml-0.5" fill="#fff" />}
                      </button>
                    )}
                    <span className="absolute bottom-1.5 left-1.5 z-10 text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-[#ffd21e] text-black" data-testid={`myvideo-category-${m.id}`}>
                      {m.share_category || m.category}
                    </span>
                  </div>
                  {statById[m.id] && (
                    <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] font-semibold text-white flex-wrap" data-testid={`myvideo-metrics-${m.id}`}>
                      <span className="flex items-center gap-1" title="Views"><Eye size={12} className="text-white" /> {statById[m.id].views}</span>
                      <span className="flex items-center gap-1" title="Rating"><Star size={12} className="text-white" /> {statById[m.id].rating_avg || "—"}</span>
                      <span className="flex items-center gap-1" title="Loves" data-testid={`myvideo-loves-${m.id}`}><Heart size={12} className="text-white" /> {statById[m.id].loves || 0}</span>
                      {m.format !== "podcast" && m.has_video && (
                      <select
                        data-testid={`myvideo-price-${m.id}`}
                        title="Set Asset Price"
                        value={statById[m.id].bundle_price != null ? String(statById[m.id].bundle_price) : ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { e.stopPropagation(); savePrice(m.id, e.target.value); }}
                        className="appearance-none text-[11px] font-extrabold rounded-full border border-white/40 bg-black/25 text-white px-2 py-0.5 cursor-pointer outline-none hover:bg-black/35 transition-colors"
                      >
                        {statById[m.id].bundle_price == null && <option value="">Set Asset Price</option>}
                        <option value="0">Free</option>
                        {PRICE_OPTS.map((o) => <option key={o} value={String(o)}>${o.toFixed(2)}</option>)}
                      </select>
                      )}
                      {(statById[m.id].flags > 0 || statById[m.id].hidden) && (
                        <span className="flex items-center gap-1 text-[#ffd7d7]" title="Flags / moderation"><Flag size={12} /> {statById[m.id].hidden ? "Hidden" : statById[m.id].flags}</span>
                      )}
                      <span className="flex items-center gap-1 ml-auto text-white" title="Tips received" data-testid={`myvideo-tips-${m.id}`}><Coins size={12} className="text-white" /> ${(((statById[m.id].tips || 0)) / 100).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8" data-testid="videos-pagination">
              <button
                data-testid="videos-prev-page"
                disabled={safePage <= 1}
                onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="text-sm font-bold px-4 py-2 rounded-full border-2 border-[#5d8a52] text-[#3f6238] bg-white hover:bg-[#eef3ea] disabled:opacity-40 disabled:hover:bg-white transition-colors"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((n) => (
                <button
                  key={n}
                  data-testid={`videos-page-${n}`}
                  onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className={`w-9 h-9 rounded-full text-sm font-extrabold transition-colors ${n === safePage ? "bg-[#4f7746] text-white" : "bg-white border-2 border-[#5d8a52] text-[#3f6238] hover:bg-[#eef3ea]"}`}
                >
                  {n}
                </button>
              ))}
              <button
                data-testid="videos-next-page"
                disabled={safePage >= totalPages}
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="text-sm font-bold px-4 py-2 rounded-full border-2 border-[#5d8a52] text-[#3f6238] bg-white hover:bg-[#eef3ea] disabled:opacity-40 disabled:hover:bg-white transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      ))}

      {showTrash && (
        <div className="mt-2" data-testid="trash-panel">
          <button
            data-testid="trash-back-btn"
            onClick={() => setShowTrash(false)}
            className="text-sm font-semibold text-[#4f7746] hover:text-[#3f6238] flex items-center gap-1.5 mb-4 transition-colors"
          >
            <RotateCcw size={15} /> Back to my videos
          </button>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-lg font-extrabold flex items-center gap-2 text-white sms-outline-dark">
              <Trash2 size={18} /> Recently Deleted
            </h2>
            <span className="text-xs text-[#6b6880]">Kept for 14 days · then removed automatically</span>
          </div>
          {trash.length === 0 ? (
            <div className="rounded-3xl p-10 text-center text-white/70" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="trash-empty">
              Nothing here — your deleted videos will show up on this page.
            </div>
          ) : (
          <div className="space-y-2.5">
            {trash.slice(0, 30).map((m) => {
              const daysLeft = m.deleted_at
                ? Math.max(0, 14 - Math.floor((Date.now() - new Date(m.deleted_at).getTime()) / 86400000))
                : 14;
              return (
              <div
                key={m.id}
                data-testid={`trash-item-${m.id}`}
                className="sms-card w-full p-2 flex items-center gap-3"
                style={{ border: "2px dashed #e0b3ba", background: "#fffafb" }}
              >
                <div className="w-16 h-16 rounded-lg bg-[#f1eff8] overflow-hidden shrink-0 opacity-80">
                  {m.hero_image_id && <img src={imageUrl(m.hero_image_id)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold leading-snug line-clamp-2 text-[#171426]" title={m.title}>{m.title || "Untitled video"}</div>
                  <div className="text-xs font-semibold mt-0.5" style={{ color: daysLeft <= 3 ? "#c22f45" : "#9a96ac" }} data-testid={`trash-countdown-${m.id}`}>
                    {daysLeft === 0 ? "Deletes today" : `Deletes in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    data-testid={`restore-trash-${m.id}`}
                    onClick={() => restoreFromTrash(m.id)}
                    disabled={trashBusy === m.id}
                    title="Recover to My Videos"
                    className="flex items-center gap-1.5 text-xs font-bold text-[#4f7746] bg-[#eef3ea] border-2 border-[#5d8a52] rounded-xl px-3 py-2 hover:bg-[#dfead8] transition-colors disabled:opacity-50"
                  >
                    {trashBusy === m.id ? <Loader2 size={14} className="spin" /> : <ArchiveRestore size={14} />} Recover
                  </button>
                  <button
                    data-testid={`purge-trash-${m.id}`}
                    onClick={() => purgeForever(m.id)}
                    disabled={trashBusy === m.id}
                    title="Delete forever"
                    aria-label="Delete forever"
                    className="p-2 rounded-xl text-[#e0455c] hover:text-[#c22f45] hover:bg-[#fdeef0] transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      <VideoUploadModal open={!!editManual} onClose={() => setEditManual(null)} onDone={load} mode="edit" manual={editManual} topControls={editManual ? renderEditControls(editManual) : null} />

      {langCopyFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80" data-testid="lang-copy-modal" onClick={() => !langCopyBusy && setLangCopyFor(null)}>
          <div className="sms-tweed w-full max-w-sm rounded-2xl bg-[#171426] border-2 border-[#5d8a52] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Languages size={18} className="text-[#ffd21e]" />
              <h2 className="text-base font-extrabold text-white">Make a language copy</h2>
            </div>
            <p className="text-xs text-white/60 mb-4 leading-relaxed">
              Creates a separate copy of <span className="text-white/85 font-bold">"{langCopyFor.title}"</span> in the language you pick. Your original stays exactly as it is. You'll finish by rendering it on its own page (an extra language is $0.50).
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(LANG_LABELS).filter(([code]) => code !== (langCopyFor.language || "en")).map(([code, label]) => (
                <button
                  key={code}
                  data-testid={`lang-copy-opt-${code}`}
                  disabled={langCopyBusy}
                  onClick={() => makeLanguageCopy(langCopyFor, code)}
                  className="flex items-center gap-2 rounded-xl border-2 border-[#3a4a34] bg-transparent hover:bg-[#5d8a52]/15 hover:border-[#5d8a52] text-white text-sm font-bold px-3 py-2.5 transition-colors disabled:opacity-50"
                >
                  <span className="text-[10px] font-extrabold text-black bg-white rounded px-1.5 py-0.5">{code.toUpperCase()}</span>
                  {label}
                </button>
              ))}
            </div>
            <button data-testid="lang-copy-cancel" onClick={() => setLangCopyFor(null)} disabled={langCopyBusy} className="mt-4 w-full rounded-xl border-2 border-white/15 text-white/70 hover:text-white text-sm font-bold py-2.5 transition-colors disabled:opacity-50">
              {langCopyBusy ? <span className="inline-flex items-center gap-2"><Loader2 size={15} className="spin" /> Creating…</span> : "Cancel"}
            </button>
          </div>
        </div>
      )}


      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog" className="border-2 border-[#5d8a52]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#c22f45]">
              <Trash2 size={20} /> Are you sure you want to remove this video?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              {confirmDel?.title ? <>“{confirmDel.title}” will move to <b>Recently Deleted</b>.</> : <>This video will move to <b>Recently Deleted</b>.</>} You can recover it for the next 14 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Keep it</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={() => { const id = confirmDel?.id; setConfirmDel(null); if (id) remove(id); }}
              className="bg-[#e0455c] hover:bg-[#c22f45] text-white"
            >
              Remove video
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmExpert} onOpenChange={(o) => { if (!o) setConfirmExpert(null); }}>
        <AlertDialogContent data-testid="expert-confirm-dialog" className="border-2 border-[#1e3aaa]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#1e3aaa]">
              <MessageCircle size={20} /> {confirmExpert?.expert?.enabled ? "Add more Expert questions?" : "Add an AI Expert to this video?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              {confirmExpert?.expert?.enabled ? (
                <>
                  This video's Expert has used <b>{confirmExpert?.expert?.used}/{confirmExpert?.expert?.quota}</b> questions.
                  Add <b>500 more</b> for <b>$1.99</b> (deducted from your balance now).
                </>
              ) : (
                <>
                  Viewers get a friendly, on-topic AI chatbot right on this video — auto-tuned to “{confirmExpert?.title}”.
                  You get <b>500 questions for $1.99</b>, deducted from your balance now. When they run out you can add more anytime.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expert-cancel-btn">Not now</AlertDialogCancel>
            <AlertDialogAction
              data-testid="expert-confirm-btn"
              disabled={expertBusy}
              onClick={(e) => { e.preventDefault(); if (confirmExpert) buyExpert(confirmExpert); }}
              className="bg-[#1e3aaa] hover:bg-[#16308c] text-white"
            >
              {expertBusy ? <Loader2 size={16} className="spin mr-1.5" /> : null}
              {confirmExpert?.expert?.enabled ? "Add 500 — $1.99" : "Enable — $1.99"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {preview && (
        <div
          data-testid="video-preview-overlay"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closePreview}
        >
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="video-preview-close"
              onClick={closePreview}
              className="absolute -top-11 right-0 text-white/90 hover:text-white flex items-center gap-1.5 font-semibold"
            >
              <X size={20} /> Close
            </button>
            <video
              data-testid="video-preview-player"
              src={preview.url}
              controls
              autoPlay
              playsInline
              controlsList="nofullscreen"
              className="w-full max-h-[80vh] rounded-2xl bg-black"
            />
            <p className="text-center text-white/85 text-sm font-semibold mt-3 truncate">{preview.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
